import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import DashboardTopbar from "components/DashboardTopbar";
import AccountSelectModal from "components/AccountSelectModal";
import { useAppContext } from "components/Context/AppContext";
import "styles/dashboard.css";
import "./master.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Alert icon (used by both message banner options) ──────────────────────────
const IconAlert = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

// ── SVG icons (stroke="var(--dash-gold)") ─────────────────────────────────────
const IconAccounts = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconSummary = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);
const IconHoldings = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);
const IconRisk = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
    <polyline points="22 12 18 12 15 18 9 6 6 12 2 12"/>
  </svg>
);
const IconStress = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);
const IconWhatIf = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
  </svg>
);
const IconHistorical = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 18 9 6 6 12 2 12"/>
  </svg>
);
const IconSettings = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
  </svg>
);
const IconPortfolios = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
const IconAlternatives = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 3a9 9 0 0 1 0 18"/>
    <path d="M12 12l6-3"/>
  </svg>
);
const IconSecurityLevel = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="var(--dash-gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="9" y1="4" x2="9" y2="20"/>
  </svg>
);

// ── Card definitions ───────────────────────────────────────────────────────────
const LINK_CARDS = [
  {
    id: "summary",
    title: "The Big Picture",
    desc: "Portfolio overview, risk metrics, asset allocation, broker breakdown and consolidated accounts.",
    to: "/summary",
    Icon: IconSummary,
  },
  {
    id: "holdings",
    title: "Holdings",
    desc: "Full positions table, historical market value, allocation drill-down by asset class, region and more.",
    to: "/holdings",
    Icon: IconHoldings,
  },
  {
    id: "risk",
    title: "Risk",
    desc: "VaR, ES, allocation drill-down, concentrations vs limits and top risk contributors.",
    to: "/risk",
    Icon: IconRisk,
  },
  {
    id: "alternatives",
    title: "Alternatives",
    desc: "Illiquid sub-class breakdown, allocation vs VaR donuts, liquidity-adjusted gauges and positions.",
    to: "/alternatives",
    Icon: IconAlternatives,
  },
  {
    id: "stress",
    title: "Stress Scenarios",
    desc: "Historical and hypothetical stress tests, scenario P&L impact and tail risk analysis.",
    to: "/stress",
    Icon: IconStress,
  },
  {
    id: "whatif",
    title: "What-if Analysis",
    desc: "Compare portfolio allocations, model risk/return scenarios and analyse alternative positions.",
    to: "/whatif",
    Icon: IconWhatIf,
  },
  {
    id: "historical",
    title: "Historical",
    desc: "Track portfolio evolution over time — metrics, concentrations and allocations across snapshots.",
    to: "/historical",
    Icon: IconHistorical,
  },
  {
    id: "security-level",
    title: "Security Level",
    desc: "Drill into individual securities — class, region, currency, market value and full risk metrics per position.",
    to: "/security-level",
    Icon: IconSecurityLevel,
  },
  {
    id: "settings",
    title: "Settings",
    desc: "Configure limits, parameters, benchmarks and risk thresholds per portfolio.",
    to: "/settings",
    Icon: IconSettings,
  },
  {
    id: "portfolios",
    title: "Portfolios",
    desc: "Manage current portfolios and upload new ones from the input template or an account statement.",
    to: "/portfolios",
    Icon: IconPortfolios,
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────
export default function MasterDashboard() {
  const { selectedAccount } = useAppContext();
  const [accModalOpen, setAccModalOpen] = useState(false);
  const [dashboardMsg, setDashboardMsg] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    fetch(`${DASHBOARD_API}/dashboard/message?token=${token}`)
      .then((r) => r.json())
      .then((d) => setDashboardMsg(d.message || null))
      .catch(console.error);
  }, []);

  const accountName =
    selectedAccount?.short_name || selectedAccount?.account_name || "TRG";
  const eyebrow = `${accountName} Dashboard`;

  return (
    <>
      <Helmet>
        <title>Dashboard — Tail Risk Global</title>
      </Helmet>

      <div className="master-shell">

        <DashboardTopbar />

        <div className="master-body">

          {/* Header */}
          <div style={{ textAlign: "center" }}>
            <div className="master-eyebrow">{eyebrow}</div>
            <div className="master-subtitle">
              Your risk and portfolio analytics — all in one place. Select a module to get started.
            </div>
          </div>

          {/* Message banner */}
          {dashboardMsg && (
            <div className="master-msg-banner">
              <IconAlert />
              <span>{dashboardMsg}</span>
            </div>
          )}

          {/* Card grid */}
          <div className="master-cards">

            {/* Accounts card — opens AccountSelectModal */}
            <div className="master-card" onClick={() => setAccModalOpen(true)}>
              <div className="master-card-icon"><IconAccounts /></div>
              <div className="master-card-title">Accounts</div>
              <div className="master-card-desc">
                Select and switch between your assigned portfolios.
              </div>
              <div className="master-card-arrow">→</div>
            </div>

            {/* Link cards */}
            {LINK_CARDS.map(({ id, title, desc, to, Icon }) => (
              <Link key={id} className="master-card" to={to}>
                <div className="master-card-icon"><Icon /></div>
                <div className="master-card-title">{title}</div>
                <div className="master-card-desc">{desc}</div>
                <div className="master-card-arrow">→</div>
              </Link>
            ))}

          </div>
        </div>

        <div className="master-footer">
          Tail Risk Global &nbsp;·&nbsp; Confidential &nbsp;·&nbsp; For internal use only
        </div>

      </div>

      {accModalOpen && (
        <AccountSelectModal onDone={() => setAccModalOpen(false)} />
      )}
    </>
  );
}
